import { gasCustomerStore } from "../../../store/store";
import { FormView } from "../../UI/view/form-view";
import { FormItemType } from "../../UI/view/component-map";
import { createView } from "../../UI/view/view";

import { customerPermissions } from "./customer-data-source";
import { getCOCReports, getCoordinates, getDeliveryTypes } from "../../../services/requests";

export const customerGasCreate = {
  permissions: customerPermissions,
  items: [
    {
      type: FormItemType.fieldset,
      label: 'Details',
      items: [
        {
          type: FormItemType.row,
          items: [
            {
              label: 'Customer ID',
              name: 'customerId',
              required: true,
              unique: true,
              number: true,
              min: 1
            },
            {
              label: 'Name',
              name: 'customerName',
              required: true,
              unique: true
            },
            {
              label: 'Status',
              name: 'status',
              type: FormItemType.select,
              required: true,
              options: [{ text: "", id: null }, { text: "Active", id: "Active" }, { text: "In-Active", id: "In-Active" }]
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              label: 'Address',
              name: 'address',
              required: true,
              tooltip: `If changing address please regenerate lat/long`
            },
            {
              label: 'Address 2',
              name: 'address2',
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              label: 'City',
              name: 'city',
              required: true,
            },
            {
              label: 'State',
              name: 'state',
              required: true,
            },
            {
              label: 'Zip',
              name: 'zip',
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'latitude',
              label: 'Latitude',
              number: true,
              disabled: ({ watch }) => !watch('overrideLatLong')
            },
            {
              name: 'longitude',
              label: 'Longitude',
              number: true,
              disabled: ({ watch }) => !watch('overrideLatLong')
            },
            {
              type: FormItemType.checkboxField,
              label: 'Override auto-generated lat/long',
              name: 'overrideLatLong'
            }
          ]
        },
        {
          type: FormItemType.button,
          label: 'Generate Lat/Long',
          style: { marginBottom: 6 },
          handler: async ({ watch, setValue, getValues }) => {
            const { address: address1, address2, city, state, zip } = getValues()
            const address = `${address1 ?? ''} ${address2 ?? ''}, ${city ?? ''},${state ?? ''} ${zip ?? ''}`;
            const coordinates = await getCoordinates(encodeURIComponent(address));
            if (coordinates) {
              setValue('latitude', coordinates?.latitude);
              setValue('longitude', coordinates?.longitude);
            }
          }
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'poNumber',
              label: 'PO Number'
            },
            {
              name: 'releaseOrderNumber',
              label: 'Release Order Number'
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'specs',
              label: 'Specs',
              required: true,
            },
            {
              name: 'specialInstructions',
              label: 'Special Instructions'
            },
            {
              name: 'bdtcpo',
              label: 'BDTCPO'
            },
            {
              name: 'cocid',
              label: 'COC',
              type: FormItemType.select,
              defaultValue: 0,
              required: true,
              options: async () => {
                const res = await getCOCReports();
                return [{ id: '', text: '-- Select --' }, ...res.map(rec => ({ id: rec.cocid, text: rec.cocName }))];
              }
            },
          ]
        },
        {
          name: 'coCdocumentText',
          label: 'COC Text',
          required: true,
          type: FormItemType.textarea,
          tooltip: `NOTE: If needed, please use the following input place holders (%abc%) in the COC text:
              Date = %DATE%, Container = %CONTAINER%, Order Number = %ORDERNUMBER%,
              Temperature = %TEMPERATURE%, Pressure = %PRESSURE%, Purity = %PURITY%`
        },
        {
          name: 'driverInstruction',
          type: FormItemType.textarea,
          label: 'Driver Instruction'
        },
        {
          name: 'logisticsOnlyNotes',
          type: FormItemType.textarea,
          label: 'Logistics Only Notes'
        },
        {
          name: 'driverRouting',
          type: FormItemType.textarea,
          label: 'Driver Routing'
        },
        {
          name: 'billingInstructions',
          type: FormItemType.textarea,
          label: 'Billing Instructions'
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'recommendedFillPlant',
              label: 'Recommended Fill Plant',
              type: FormItemType.select,
              options: [
                {
                  text: '-- Select --',
                  id: ''
                },
                { text: 'Algeria', id: 'Algeria' },
                { text: 'Bushton', id: 'Bushton' },
                { text: 'Tumbleweed', id: 'Tumbleweed' },
                { text: 'Exxon', id: 'Exxon' },
                { text: 'Qatar', id: 'Qatar' },
                { text: 'Ulysses', id: 'Ulysses' },
                { text: 'Shrewsbury', id: 'Shrewsbury' },
                { text: 'Taft', id: 'Taft' },
                { text: 'Keasbey', id: 'Keasbey' },
                { text: 'Fife', id: 'Fife' },
                { text: 'Ontario', id: 'Ontario' },
                { text: 'North Royalton', id: 'North Royalton' },
                { text: 'East Chicago', id: 'East Chicago' },
                { text: 'Tampa', id: 'Tampa' },
                { text: 'Waxahachie', id: 'Waxahachie' },
              ]
            },
            {
              name: 'defaultDeliveryTypeId',
              label: 'Default Delivery Type',
              type: FormItemType.select,
              options: async () => {
                const dt = await getDeliveryTypes(0);
                return [{ id: '', text: '-- Select --' }, ...dt.map(rec => ({ id: rec.id, text: rec.name }))];
              }
            },
            {
              name: 'region',
              label: 'Region',
              required: true,
              type: FormItemType.select,
              options: [
                { id: '', text: '-- Select --' },
                { id: 'Internal', text: 'Internal' },
                { id: 'KS Direct', text: 'KS Direct' },
                { id: 'PDI', text: 'PDI' },
                { id: 'Praxair Electronics', text: 'Praxair Electronics' },
                { id: 'Praxair Mexico', text: 'Praxair Mexico' },
                { id: 'Praxair South America', text: 'Praxair South America' },
                { id: 'Repair Shop', text: 'Repair Shop' },
                { id: 'US Direct', text: 'US Direct' },
                { id: 'US Gov', text: 'US Gov' },
              ]
            },
            {
              name: 'aveNetVol',
              label: 'Avg Net Volume',
              required: true,
              number: true
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'hourlyMaintenanceRate',
              label: 'Hourly Maintenance Rate',
              number: true,
              defaultValue: 0,
              required: true
            },
            {
              name: 'freeRentDays',
              label: 'Free Days',
              number: true,
              defaultValue: 0,
              required: true
            },
            {
              name: 'rentalFeePerDay',
              label: 'Rental $/Day',
              number: true,
              defaultValue: 0,
              required: true
            },
            {
              name: 'rentalFeePerMonth',
              label: 'Rental $/Month',
              number: true,
              defaultValue: 0,
              required: true
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'billingAddress1',
              label: 'Billing Address 1'
            },
            {
              name: 'billingAddress2',
              label: 'Billing Address 2'
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'billingAddress3',
              label: 'Billing Address 3'
            },
            {
              name: 'billingAddress4',
              label: 'Billing Address 4'
            },
            {
              name: 'billingAddress5',
              label: 'Billing Address 5'
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'invoiceCustomerCode',
              label: 'Invoice Customer Code'
            },
            {
              name: 'invoiceTC',
              label: 'Invoice TC'
            },
            {
              name: 'invoiceCO',
              label: 'Invoice CO'
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'invoiceCountry',
              label: 'Invoice Country'
            },
            {
              name: 'invoiceLoc',
              label: 'Invoice Loc'
            },
            {
              name: 'invoiceTermsCode',
              label: 'Invoice Terms Code'
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'invoiceTermsofPayment',
              label: 'Invoice Terms of Payment'
            },
            {
              name: 'invoiceTermsofSale',
              label: 'Invoice Terms of Sale'
            },
            {
              name: 'invoiceRemittanceInfo',
              label: 'Invoice Remittance Info'
            },

          ]
        },
        {
          name: 'miles',
          label: 'Miles',
          number: true
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'waterVolumeBankA',
              label: 'Water Vol Bank A',
              number: true
            },
            {
              name: 'waterVolumeBankB',
              label: 'Water Vol Bank B',
              number: true
            },
            {
              name: 'waterVolumeBankC',
              label: 'Water Vol Bank C',
              number: true
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'waterVolumeBankD',
              label: 'Water Vol Bank D',
              number: true
            },
            {
              name: 'waterVolumeBankE',
              label: 'Water Vol Bank E',
              number: true
            },
            {
              name: 'waterVolumeBankF',
              label: 'Water Vol Bank F',
              number: true
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'billingPoNumberRequired',
              label: 'PO# Required for Billing',
              type: FormItemType.checkboxField
            },
            {
              name: 'billingRoNumberRequired',
              label: 'RO# Required for Billing',
              type: FormItemType.checkboxField
            },
            {
              name: 'bypassGasBilling',
              label: 'Bypass JDE Billing',
              type: FormItemType.checkboxField

            },
          ]
        },
        {
          name: 'bypassGasBillingReason',
          label: 'Reason for Bypass',
          type: FormItemType.textarea
        },
        {
          name: 'billingComments',
          label: 'Billing Comments',
          type: FormItemType.textarea
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'containerReturnEmail',
              label: 'Send Email When Container Returned?',
              type: FormItemType.select,
              options: [
                { id: '', text: '-- Select --' },
                { id: 'Yes', text: 'Yes' },
                { id: 'No', text: 'No' }
              ]
            },
            {
              name: 'containerShipEmail',
              label: 'Send Email When Container Shipped?',
              type: FormItemType.select,
              options: [
                { id: '', text: '-- Select --' },
                { id: 'Yes', text: 'Yes' },
                { id: 'No', text: 'No' }
              ]
            },
            {
              name: 'emailAddress',
              label: 'Email Address'
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'maxResidualPct',
              label: 'Max Residual %'
            },
            {
              name: 'minShipPressure',
              label: 'Min Shipping Pressure (PSI)',
              number: true,
              defaultValue: 0,
              required: true
            },
            {
              name: 'residualPenaltyPressureThreshold',
              label: 'Residual Pressure Threshold ',
              number: true,
              defaultValue: 0,
              required: true,
              min: 0
            },
          ]
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'bypassContaminentBilling',
              label: 'Ignore Contaminents',
              type: FormItemType.checkboxField
            },
            {
              name: 'crudeSource',
              label: 'Crude Source',
              type: FormItemType.checkboxField
            },
            {
              name: 'compressorOnSite',
              label: 'Compressor On Site',
              type: FormItemType.checkboxField
            },
            {
              name: 'residualPenaltyApplied',
              label: 'Residual Penalty Applied',
              type: FormItemType.checkboxField
            },
          ]
        },
      ]
    }
  ]

} as FormView;

export const CustomerGasCreate = createView({ form: customerGasCreate as any, repository: gasCustomerStore as any });